import { jsPDF } from "jspdf/dist/jspdf.umd";

const defineEntityType = (entity) => {
	switch (entity) {
		case "userId":
			return "Atendente";
		case "queueId":
			return "Setor";
		case "whatsappId":
			return "Conexão";
		case "status":
			return "Status";
		default:
			return "Grupo";
	}
};

const sanitizeText = (value, fallback = "—") => {
	if (value == null) return fallback;

	const normalized = String(value)
		.replace(/<[^>]+>/g, " ")
		.replace(/\s+/g, " ")
		.trim();

	return normalized || fallback;
};

const breakLongTokens = (value, size = 32) =>
	value.replace(new RegExp(`([^\\s-]{${size}})`, "g"), "$1 ");

const toPdfSafeText = (value, fallback = "—", maxLength = 420) => {
	const normalized = sanitizeText(value, fallback);
	if (normalized === fallback) {
		return fallback;
	}

	return breakLongTokens(normalized.slice(0, maxLength), 32);
};

const buildGroupLabel = (groupBy, key, firstTicket) => {
	if (key === "unassigned") {
		return "Não atribuído";
	}

	switch (groupBy) {
		case "userId":
			return `${firstTicket?.user?.id || "-"} - ${sanitizeText(key, "-")}`;
		case "queueId":
			return `${firstTicket?.queue?.id || "-"} - ${sanitizeText(key, "-")}`;
		case "whatsappId":
			return `${firstTicket?.whatsapp?.id || "-"} - ${sanitizeText(key, "-")}`;
		case "status":
			return sanitizeText(key, "-");
		default:
			return sanitizeText(key, "-");
	}
};

const normalizeGroups = (ticketsByGroup, groupBy) => {
	const groupedTickets =
		ticketsByGroup && typeof ticketsByGroup === "object" ? ticketsByGroup : {};

	return Object.entries(groupedTickets).map(([key, tickets]) => {
		const safeTickets = Array.isArray(tickets) ? tickets : [];
		const firstTicket = safeTickets[0];

		return {
			key,
			title: `${defineEntityType(groupBy)}: ${buildGroupLabel(groupBy, key, firstTicket)}`,
			tickets: safeTickets.map((ticket, index) => {
				const tags = Array.isArray(ticket?.contact?.tags)
					? ticket.contact.tags
							.map((tag) => toPdfSafeText(tag?.name, "", 80))
							.filter(Boolean)
							.join(", ")
					: "";

				const messages = Array.isArray(ticket?.messages)
					? ticket.messages
							.map((message) => toPdfSafeText(message?.body, "", 240))
							.filter(Boolean)
					: [];

				return {
					id: ticket?.id || `${key}-${index}`,
					queueName: toPdfSafeText(ticket?.queue?.name, "Sem setor", 120),
					userName: toPdfSafeText(ticket?.user?.name, "Sem atendente", 120),
					connectionName: toPdfSafeText(ticket?.whatsapp?.name, "Sem conexão", 120),
					contactName: toPdfSafeText(ticket?.contact?.name, "Sem contato", 120),
					tags: tags || "—",
					messages:
						messages.length > 0
							? messages.slice(0, 6).join("\n")
							: "Sem mensagens.",
				};
			}),
		};
	});
};

export const exportPdfReport = ({ ticketsByGroup, groupBy, fileName }) => {
	const groups = normalizeGroups(ticketsByGroup, groupBy);
	const doc = new jsPDF({ unit: "mm", format: "a4" });
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const marginX = 14;
	const bottomMargin = 14;
	const contentWidth = pageWidth - marginX * 2;
	let cursorY = 18;

	const ensureSpace = (requiredHeight = 8) => {
		if (cursorY + requiredHeight <= pageHeight - bottomMargin) {
			return;
		}

		doc.addPage();
		cursorY = 18;
	};

	const writeText = (
		text,
		{
			fontSize = 10,
			fontStyle = "normal",
			color = [24, 24, 27],
			spacingAfter = 2,
			indent = 0,
			maxWidth = contentWidth - indent,
		} = {}
	) => {
		doc.setFont("helvetica", fontStyle);
		doc.setFontSize(fontSize);
		doc.setTextColor(color[0], color[1], color[2]);

		const lines = doc.splitTextToSize(String(text || "—"), maxWidth);
		const lineHeight = Math.max(4.2, fontSize * 0.42 + 0.7);
		const requiredHeight = lines.length * lineHeight + spacingAfter;
		ensureSpace(requiredHeight);
		doc.text(lines, marginX + indent, cursorY);
		cursorY += requiredHeight;
	};

	const drawDivider = () => {
		ensureSpace(4);
		doc.setDrawColor(228, 228, 231);
		doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
		cursorY += 4;
	};

	writeText("Relatório de tickets", {
		fontSize: 16,
		fontStyle: "bold",
		spacingAfter: 3,
	});
	writeText(`Agrupado por ${defineEntityType(groupBy).toLowerCase()}.`, {
		fontSize: 10,
		color: [82, 82, 91],
		spacingAfter: 6,
	});

	if (groups.length === 0) {
		writeText("Nenhum ticket encontrado para exportação.", {
			fontSize: 10,
			color: [113, 113, 122],
		});
		doc.save(fileName);
		return;
	}

	groups.forEach((group, groupIndex) => {
		if (groupIndex > 0) {
			drawDivider();
		}

		writeText(group.title, {
			fontSize: 12,
			fontStyle: "bold",
			spacingAfter: 3,
		});

		if (!group.tickets.length) {
			writeText("Nenhum ticket neste grupo.", {
				fontSize: 10,
				color: [113, 113, 122],
				spacingAfter: 4,
			});
			return;
		}

		group.tickets.forEach((ticket, index) => {
			writeText(`Ticket #${sanitizeText(ticket.id, "-")}`, {
				fontSize: 10,
				fontStyle: "bold",
				spacingAfter: 1,
			});
			writeText(`Contato: ${ticket.contactName}`);
			if (groupBy !== "queueId") {
				writeText(`Setor: ${ticket.queueName}`);
			}
			if (groupBy !== "userId") {
				writeText(`Atendente: ${ticket.userName}`);
			}
			if (groupBy !== "whatsappId") {
				writeText(`Conexão: ${ticket.connectionName}`);
			}
			writeText(`Etiquetas: ${ticket.tags}`);
			writeText(`Mensagens: ${ticket.messages}`, {
				spacingAfter: 3,
			});

			if (index < group.tickets.length - 1) {
				drawDivider();
			}
		});
	});

	doc.save(fileName);
};
